import React from "react";

import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { faClipboardListCheck } from "@fortawesome/pro-regular-svg-icons";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { PropTypes } from "prop-types";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import { OutlineButton } from "components/Button";
import { SyncingStatus } from "components/Card/Elements";
import { Column, Row } from "components/Layout";
import { ResponsiveText } from "components/ResponsiveText";

import { PenTypes } from "constants/auctionPens";
import { userTypes } from "constants/users";

import { ForUserType } from "containers/ForUserType";

import { scanLotConfig } from "lib/scanLot";

import { scanRowWeightTypes, selectScanRowData } from "selectors";

import { FaIcon } from "./Elements";

const Wrapper = styled.div`
  padding: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.gray95};
  }
`;

const Or = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.error};
  padding: 0 12px;
`;

const Text = styled.span`
  font-size: ${({ prominent }) => (prominent ? 14 : 12)}px;
  font-weight: ${({ bold, prominent }) => (bold || prominent ? 700 : 400)};
  margin-bottom: 3px;
  ${({ light }) => light && "color: #a1a1a1"};
`;

const ButtonText = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  margin: 0 6px;
`;

const EqualColumn = styled(Column)`
  flex: 1;
`;

/* React dev tools note - this incorrectly flags itself as a re-render in
 * dev tools, but, it's not - looks like a hiccup with redux and (not) hooks.
 * https://github.com/facebook/react-devtools/issues/1290
 * */
const ScanRow = React.memo(
  ({
    destinationConsignmentId,
    disallowTaken,
    eidOrNlisId,
    nlisId,
    onEdit,
    onIgnore,
    onKeep,
    onSelect,
    isExpanded,
    showEdit,
    showIgnore,
    showKeep,
    showSelect,
    syncing,
    penType,
    isArrivalOrPenScanning,
    scanLotId,
    showDuplicate,
  }) => {
    const {
      getLotById,
      getNextPenTitleByPenIdOrPenArchetypeId,
      lotPenIdReference,
      lotMarksReference,
    } = scanLotConfig(penType);

    const scanLot = useSelector(getLotById(scanLotId));

    const scanLotPenTitle = useSelector(
      getNextPenTitleByPenIdOrPenArchetypeId(scanLot?.[lotPenIdReference]),
    );

    const {
      agencyName,
      buyerName,
      buyerWayName,
      eid,
      isEidTaken,
      isDuplicate,
      isSaleyardScanned,
      lotNumber,
      derivedNlisId,
      nlisPic,
      marks,
      penName,
      derivedSyncing,
      vendorName,
      vendorCode,
      derivedWeight,
    } = useSelector(state =>
      selectScanRowData(state, {
        destinationConsignmentId,
        eidOrNlisId,
        showDuplicate,
      }),
    );

    const displayNlisId = nlisId || derivedNlisId;
    const isSyncing = syncing || derivedSyncing;

    function onClickKeep() {
      typeof onKeep === "function" && onKeep(eid);
    }
    function onClickEdit() {
      typeof onEdit === "function" && onEdit(eid);
    }
    function onClickIgnore() {
      typeof onIgnore === "function" && onIgnore(eid);
    }
    function onClickSelect() {
      typeof onSelect === "function" && onSelect(eid);
    }
    const isEidEditable = !isArrivalOrPenScanning && isDuplicate && isExpanded;
    const canKeep =
      showKeep && ((disallowTaken && !isEidTaken) || !disallowTaken);
    const isTakenError = disallowTaken && isEidTaken;
    const isNewScan = !isDuplicate && !isSaleyardScanned;
    const nlisDataAvailable = displayNlisId || nlisPic;

    const displayEid = eid || "(EID Pending)";

    const duplicateText = () => {
      if (isSaleyardScanned) {
        return "This EID was scanned into another Consignment.";
      } else if (
        isArrivalOrPenScanning &&
        penType === PenTypes.RECEIVING &&
        scanLot
      ) {
        return `This EID was scanned into another Receival Lot from Receival Pen ${scanLotPenTitle} with Mark ${scanLot[lotMarksReference]}.`;
      } else if (
        isArrivalOrPenScanning &&
        penType === PenTypes.SELLING &&
        scanLot
      ) {
        return `This EID was scanned into another Pen Scan Lot from Selling Pen ${scanLotPenTitle} with Marks ${scanLot[
          lotMarksReference
        ]
          .map(mark => mark.location)
          .join(", ")}.`;
      } else {
        return `This EID has already been scanned${
          lotNumber ? ` in Lot ${lotNumber}` : ""
        }.`;
      }
    };

    return (
      <Wrapper>
        <Row justifyBetween alignCenter flexWrap gridGap={1}>
          <Row baseline>
            {isTakenError && (
              <FaIcon
                icon={faExclamationCircle}
                color="warning"
                title="This EID cannot be changed as it has already been taken."
              />
            )}
            {isDuplicate && !isTakenError && (
              <FaIcon
                icon={faExclamationCircle}
                color="error"
                title="This EID already exists."
              />
            )}
            {isSaleyardScanned && (
              <FaIcon
                icon={faClipboardListCheck}
                color="primary"
                title="This EID has been scanned into a Consignment."
              />
            )}
            {isNewScan && (
              <FaIcon icon={faPlus} color="success" title="This EID is new." />
            )}
            {displayEid}
          </Row>
          {isSyncing && (
            <Row alignCenter>
              <SyncingStatus />
            </Row>
          )}
          {isEidEditable && (
            <Row alignCenter flexWrap>
              {canKeep && (
                <OutlineButton
                  data-tour={`keep.${displayEid}`}
                  color="error"
                  onClick={onClickKeep}
                >
                  <ButtonText>Keep EID</ButtonText>
                </OutlineButton>
              )}
              {canKeep && (showEdit || showIgnore) && <Or>or</Or>}
              {showEdit && (
                <OutlineButton color="error" onClick={onClickEdit}>
                  <ButtonText>Edit EID</ButtonText>
                </OutlineButton>
              )}
              {showIgnore && showEdit && <Or>or</Or>}
              {showIgnore && (
                <OutlineButton
                  data-tour={`ignore.${displayEid}`}
                  color="error"
                  onClick={onClickIgnore}
                >
                  <ButtonText>Ignore</ButtonText>
                </OutlineButton>
              )}
              {showSelect && (showEdit || showIgnore) && !isSaleyardScanned && (
                <Or>or</Or>
              )}
              {showSelect && !isSaleyardScanned && (
                <OutlineButton
                  data-tour={`selectSaleLot.${displayEid}`}
                  color="error"
                  onClick={onClickSelect}
                >
                  <ButtonText>Select Sale Lot</ButtonText>
                </OutlineButton>
              )}
            </Row>
          )}
        </Row>

        {nlisDataAvailable && isExpanded && (
          <Row alignCenter>
            {displayNlisId && <Text light>{displayNlisId}</Text>}
            {displayNlisId && nlisPic && "\u00A0-\u00A0"}
            {nlisPic && <Text light>{nlisPic}</Text>}
          </Row>
        )}
        {isTakenError && (
          <Row>
            <Text bold>
              This EID has already been taken, so cannot be re-assigned.
            </Text>
          </Row>
        )}
        {((!isTakenError && isDuplicate) ||
          isExpanded ||
          isSaleyardScanned) && (
          <>
            {isDuplicate && (
              <Row>
                <Text prominent>{duplicateText()}</Text>
              </Row>
            )}
            <Row gridGap={2}>
              <EqualColumn>
                {!!buyerName && <Text light>Buyer: {buyerName}</Text>}
                {!!buyerWayName && <Text light>Buyer Way: {buyerWayName}</Text>}
                <ForUserType
                  userType={[
                    userTypes.LIVESTOCK_AGENT,
                    userTypes.SALEYARD_ADMIN,
                  ]}
                >
                  {!!derivedWeight && (
                    <Text light>
                      <ResponsiveText
                        mobile={
                          derivedWeight.type === scanRowWeightTypes.AVERAGE
                            ? "Avg. Wt.: "
                            : "Single Wt.: "
                        }
                        desktop={
                          derivedWeight.type === scanRowWeightTypes.AVERAGE
                            ? "Average Weight: "
                            : "Single Weighed: "
                        }
                      />
                      {derivedWeight.value}
                    </Text>
                  )}
                </ForUserType>
              </EqualColumn>
              <EqualColumn>
                <ForUserType
                  userType={[
                    userTypes.LIVESTOCK_AGENT,
                    userTypes.SALEYARD_ADMIN,
                  ]}
                >
                  {!!marks && <Text light>Marks: {marks}</Text>}
                </ForUserType>
              </EqualColumn>
              <EqualColumn>
                <ForUserType userType={[userTypes.SALEYARD_ADMIN]}>
                  {!!agencyName && <Text light>Agency: {agencyName}</Text>}
                </ForUserType>
                {!!vendorName && (
                  <Text light>
                    Vendor: {vendorName}
                    {vendorCode && ` (${vendorCode})`}
                  </Text>
                )}
                <ForUserType
                  userType={[
                    userTypes.LIVESTOCK_AGENT,
                    userTypes.SALEYARD_ADMIN,
                  ]}
                >
                  {!!penName && (
                    <Text light>
                      <ResponsiveText mobile="Pen:" desktop="Auction Pen:" />{" "}
                      {penName}
                    </Text>
                  )}
                </ForUserType>
              </EqualColumn>
            </Row>
          </>
        )}
      </Wrapper>
    );
  },
);

// 982 123710691990

ScanRow.propTypes = {
  destinationConsignmentId: PropTypes.string,
  eidOrNlisId: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
  isArrivalOrPenScanning: PropTypes.bool,
  nlisId: PropTypes.string,
  penType: PropTypes.string,
  scanLotId: PropTypes.string,
  disallowTaken: PropTypes.bool,
  onEdit: PropTypes.func,
  onIgnore: PropTypes.func,
  onKeep: PropTypes.func,
  onSelect: PropTypes.func,
  showDuplicate: PropTypes.bool,
  showEdit: PropTypes.bool,
  showIgnore: PropTypes.bool,
  showKeep: PropTypes.bool,
  showSelect: PropTypes.bool,
  syncing: PropTypes.bool,
};

export default ScanRow;
